<script setup lang="ts">
/* eslint-disable max-lines */
import { computed, reactive, ref, watch } from 'vue'
import { useMapPointCloudSettingsStore } from '@/stores/mapPointCloudSettingsStore'
import { useMapViewSettingsStore } from '@/stores/mapViewSettingsStore'
import { JUDGEMENT_STATUS } from '@/constants/ui/map/legendConstants'
import {
  RESIDENTIAL_DAMAGE_ITEMS,
  AI_COLLAPSE_JUDGMENT_ITEMS,
  EMERGENCY_INSPECTION_ITEMS
} from '@/constants/ui/map/mapConstants'
import { useMapPointCloudStore } from '@/stores/mapPointCloudStore'
import { useGeoserverStatusStore } from '@/stores/geoserverStatusStore'
import { GEO_SERVER_STATUS } from '@/constants/api/geoserver/geoserverConstants'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'

/** 地図設定の種別 */
enum MAP_SETTING_TYPE {
  inspectionInfo = 'inspectionInfo', // 調査情報
  mapSetting = 'mapSetting' // 地図設定
}

const mapPointCloudSettingsStore = useMapPointCloudSettingsStore()
const mapViewSettingsStore = useMapViewSettingsStore()
const mapPointCloudStore = useMapPointCloudStore()
const geoserverStatusStore = useGeoserverStatusStore()
const { includesFeatureOptionsEmergency } = useGovernmentSettingStore()

/** 有効フラグ */
const isEnabled = ref<boolean>(geoserverStatusStore.geoserverStatus === GEO_SERVER_STATUS.RUNNING)

/** アコーディオン表示部分の開閉フラグ */
const isAccordionOpen = reactive<Record<MAP_SETTING_TYPE, boolean>>({
  [MAP_SETTING_TYPE.inspectionInfo]: true,
  [MAP_SETTING_TYPE.mapSetting]: true
})

/**
 * ラジオボタン監視用のwatch処理をスキップするためのフラグ
 * @note チェックボックス選択時にラジオボタン選択時の処理をスキップするためにtrueにする
 */
const skipRadioWatch = ref<boolean>(false)

/**
 * アコーディオンセクションの開閉状態を切り替える
 * @param section 切り替え対象セクション
 * @returns なし
 */
const toggleSection = (section: MAP_SETTING_TYPE): void => {
  isAccordionOpen[section] = !isAccordionOpen[section]
}

/**
 * チェックボックス変更共通ハンドラ
 * @param e クリックイベント
 * @param storeObj チェックボックスに紐づくStoreオブジェクト
 * @param key チェックボックスに紐づくStoreオブジェクトのキー
 * @returns なし
 * @note 選択された値をStoreに反映し、必要に応じて選択状態を更新する。
 */
const onChangeJudgedCheckbox = <T extends Record<string, boolean>>(
  e: Event,
  storeObj: T,
  key: keyof T
): void => {
  const target = e.target as HTMLInputElement | null
  if (!target) return
  ;(storeObj[key] as boolean) = target.checked

  // いずれかチェックされているならactiveJudgementStatusを判定済みに切替
  if (mapPointCloudSettingsStore.isAnyJudgedCheckboxChecked()) {
    skipRadioWatch.value = true
    mapPointCloudSettingsStore.activeJudgementStatus = JUDGEMENT_STATUS.JUDGED
  }
}

/**
 * ヒートマップ上に件数を表示するチェックボックス
 */
const heatmapChecked = computed({
  get: () => mapViewSettingsStore.gridLabelWide,
  set: value => {
    mapViewSettingsStore.gridLabelWide = value
    mapViewSettingsStore.gridLabelMiddle = value
  }
})

/**
 * ヒートマップ上に件数を表示するチェックボックスの処理
 * @param e クリックイベント
 */
function heatmapCheckedToggle(e: Event) {
  const target = e.target as HTMLInputElement | null
  if (!target) return
  heatmapChecked.value = target.checked
}

/**
 * ドローン空撮範囲チェックボックス
 */
const aerialAreasChecked = computed({
  get: () => mapViewSettingsStore.aerialAreasFill,
  set: value => {
    mapViewSettingsStore.aerialAreasFill = value
    mapViewSettingsStore.aerialAreasLine = value
  }
})

/**
 * ドローン空撮範囲チェックボックス変更時の処理
 * @param e クリックイベント
 */
function aerialAreasCheckedToggle(e: Event) {
  const target = e.target as HTMLInputElement | null
  if (!target) return
  aerialAreasChecked.value = target.checked
}

/** ラジオボタンの選択状態を監視 */
watch(
  () => mapPointCloudSettingsStore.activeJudgementStatus,
  newStatus => {
    // 「判定済み」ラジオが選択されたら全チェックON
    if (newStatus === JUDGEMENT_STATUS.JUDGED) {
      if (skipRadioWatch.value) {
        // チェックボックス変更時の副作用としての変更ならスキップ
        skipRadioWatch.value = false
        return
      }
      mapPointCloudSettingsStore.setAllJudgedCheckboxes(true)
    }
    // 「未判定」ラジオが選択されたら全チェックOFF
    else {
      mapPointCloudSettingsStore.setAllJudgedCheckboxes(false)
      skipRadioWatch.value = false
    }
  }
)

/** ジオサーバーの状態を監視 */
watch(
  () => geoserverStatusStore.geoserverStatus,
  newStatus => {
    isEnabled.value = newStatus === GEO_SERVER_STATUS.RUNNING
  }
)
</script>

<template>
  <div :class="{ disabled: !isEnabled }">
    <div class="map-setting">
      <!-- アコーディオンセクション -->
      <section class="accordion">
        <!-- 調査情報 -->
        <div class="accordion-item">
          <button class="accordion-header" @click="toggleSection(MAP_SETTING_TYPE.inspectionInfo)">
            <div v-if="isAccordionOpen.inspectionInfo">
              <font-awesome-icon icon="fa-caret-down" />
            </div>
            <div v-else>
              <font-awesome-icon icon="fa-caret-right" />
            </div>
            調査情報
          </button>
          <div v-if="isAccordionOpen.inspectionInfo" class="accordion-content">
            <label>
              <input type="checkbox" :checked="heatmapChecked" @change="heatmapCheckedToggle" />
              ヒートマップ上に件数を表示する
            </label>
            <div class="radio-list">
              <label>
                <input
                  type="radio"
                  :value="JUDGEMENT_STATUS.JUDGED"
                  v-model="mapPointCloudSettingsStore.activeJudgementStatus"
                />
                判定済み
              </label>
              <fieldset>
                <legend>住家被害認定調査</legend>
                <div class="checkbox-list">
                  <label v-for="item in RESIDENTIAL_DAMAGE_ITEMS" :key="item.key">
                    <input
                      type="checkbox"
                      :checked="mapPointCloudSettingsStore.residentialDamageInspection[item.key]"
                      @change="
                        e =>
                          onChangeJudgedCheckbox(
                            e,
                            mapPointCloudSettingsStore.residentialDamageInspection,
                            item.key
                          )
                      "
                    />
                    {{ item.label }}
                    [{{
                      mapPointCloudStore.invInspectionResult
                        ? mapPointCloudStore.invInspectionResult[item.key]
                        : 'ー'
                    }}/{{
                      mapPointCloudStore.invInspectionResult
                        ? mapPointCloudStore.invInspectionResult.allCount
                        : 'ー'
                    }}]
                  </label>
                </div>
              </fieldset>
              <fieldset v-if="includesFeatureOptionsEmergency()">
                <legend>応急危険度判定調査</legend>
                <div class="checkbox-list">
                  <label v-for="item in EMERGENCY_INSPECTION_ITEMS" :key="item.key">
                    <input
                      type="checkbox"
                      :checked="mapPointCloudSettingsStore.emergencyInspection[item.key]"
                      @change="
                        e =>
                          onChangeJudgedCheckbox(
                            e,
                            mapPointCloudSettingsStore.emergencyInspection,
                            item.key
                          )
                      "
                    />
                    {{ item.label }}
                    [{{
                      mapPointCloudStore.emergencyResult
                        ? mapPointCloudStore.emergencyResult[item.key]
                        : 'ー'
                    }}/{{
                      mapPointCloudStore.emergencyResult
                        ? mapPointCloudStore.emergencyResult.allCount
                        : 'ー'
                    }}]
                  </label>
                </div>
              </fieldset>
              <fieldset>
                <legend>AI倒壊判定</legend>
                <div class="checkbox-list">
                  <label v-for="item in AI_COLLAPSE_JUDGMENT_ITEMS" :key="item.key">
                    <input
                      type="checkbox"
                      :checked="mapPointCloudSettingsStore.aiCollapseJudgment[item.key]"
                      @change="
                        e =>
                          onChangeJudgedCheckbox(
                            e,
                            mapPointCloudSettingsStore.aiCollapseJudgment,
                            item.key
                          )
                      "
                    />
                    {{ item.label }}
                    [{{
                      mapPointCloudStore.aiCollapseResult
                        ? mapPointCloudStore.aiCollapseResult[item.key]
                        : 'ー'
                    }}/{{
                      mapPointCloudStore.aiCollapseResult
                        ? mapPointCloudStore.aiCollapseResult.allCount
                        : 'ー'
                    }}]
                  </label>
                </div>
              </fieldset>
              <label>
                <input
                  type="radio"
                  :value="JUDGEMENT_STATUS.NOT_JUDGED_RESIDENTIAL_DAMAGE_INSPECTION"
                  v-model="mapPointCloudSettingsStore.activeJudgementStatus"
                />
                未判定：住家被害認定調査 [{{
                  mapPointCloudStore.invInspectionResult
                    ? mapPointCloudStore.invInspectionResult.uninvestigated
                    : 'ー'
                }}/{{
                  mapPointCloudStore.invInspectionResult
                    ? mapPointCloudStore.invInspectionResult.allCount
                    : 'ー'
                }}]
              </label>
              <label v-if="includesFeatureOptionsEmergency()">
                <input
                  type="radio"
                  :value="JUDGEMENT_STATUS.NOT_JUDGED_EMERGENCY_INSPECTION"
                  v-model="mapPointCloudSettingsStore.activeJudgementStatus"
                />
                未判定：応急危険度判定調査 [{{
                  mapPointCloudStore.emergencyResult
                    ? mapPointCloudStore.emergencyResult.uninvestigated
                    : 'ー'
                }}/{{
                  mapPointCloudStore.emergencyResult
                    ? mapPointCloudStore.emergencyResult.allCount
                    : 'ー'
                }}]
              </label>
              <label>
                <input
                  type="radio"
                  :value="JUDGEMENT_STATUS.NOT_JUDGED_AI_COLLAPSE_JUDGEMENT"
                  v-model="mapPointCloudSettingsStore.activeJudgementStatus"
                />
                未判定：AI倒壊判定 [{{
                  mapPointCloudStore.aiCollapseResult
                    ? mapPointCloudStore.aiCollapseResult.uninvestigated
                    : 'ー'
                }}/{{
                  mapPointCloudStore.aiCollapseResult
                    ? mapPointCloudStore.aiCollapseResult.allCount
                    : 'ー'
                }}]
              </label>
            </div>
          </div>
        </div>
        <!-- 地図設定 -->
        <div class="accordion-item">
          <button class="accordion-header" @click="toggleSection(MAP_SETTING_TYPE.mapSetting)">
            <div v-if="isAccordionOpen.mapSetting">
              <font-awesome-icon icon="fa-caret-down" />
            </div>
            <div v-else>
              <font-awesome-icon icon="fa-caret-right" />
            </div>
            地図設定
          </button>
          <div v-if="isAccordionOpen.mapSetting" class="accordion-content">
            <div class="checkbox-list">
              <label>
                <input
                  type="checkbox"
                  :checked="aerialAreasChecked"
                  @change="aerialAreasCheckedToggle"
                />
                ドローン空撮範囲
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.orthoPhoto" />
                オルソ画像
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.aerialPhoto" />
                航空写真
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.slopeFailure" />
                土砂災害警戒区域（急傾斜地の崩壊）
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.debrisFlow" />
                土砂災害警戒区域（土石流）
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.landslide" />
                土砂災害警戒区域（地すべり）
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.floodMaxScale" />
                洪水浸水想定区域（想定最大規模）
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.floodPlannedScale" />
                洪水浸水想定区域（計画規模）
              </label>
              <label>
                <input type="checkbox" v-model="mapViewSettingsStore.tsunamiInundation" />
                津波浸水想定区域
              </label>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.map-setting {
  width: 450px;
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column;
  background: #f9f9f9;
  border-radius: 6px;
  height: 100%;
}

.accordion {
  flex-grow: 1;
  font-size: 16px;
}

.accordion-item {
  border-top: 1px solid #bbb;
}

.accordion-header {
  width: 100%;
  padding: 10px;
  background: #eee;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.accordion-content {
  padding: 10px;
  background: #fff;
}

.radio-list {
  display: flex;
  flex-direction: column;
}

legend {
  font-weight: bold;
  padding-left: 1rem;
}

.checkbox-list {
  display: flex;
  flex-direction: column;
  padding-left: 2rem;
}
</style>
